import type { ApiClient } from './ApiClient';

// SCH-03 : les 12 niveaux couvrant les trois cycles combinables (Préscolaire/
// Élémentaire/Collège) — voir apps/api/src/lib/cycles.ts, seule source de vérité
// côté backend pour la correspondance niveau -> cycle.
// SCH-13 : CI (Cours d'Initiation) ajouté — premier niveau réel du cycle
// élémentaire, avant CP (voir apps/api/src/lib/cycles.ts pour la source de vérité).
export const CLASS_LEVELS = [
  'PS',
  'MS',
  'GS',
  'CI',
  'CP',
  'CE1',
  'CE2',
  'CM1',
  'CM2',
  'SIXIEME',
  'CINQUIEME',
  'QUATRIEME',
  'TROISIEME',
] as const;
export type ClassLevel = (typeof CLASS_LEVELS)[number];

// Formes alignées sur POST/GET/PATCH/DELETE /classes (SCH-03,
// apps/api/src/routes/classes.ts) — seule source de vérité pour ces routes.
export interface SchoolClass {
  id: string;
  level: ClassLevel;
  name: string;
  schoolYear: string;
  capacity: number | null;
  // SCH-06 (pas encore implémenté) fournira le vrai décompte — toujours 0 pour
  // l'instant, affiché tel quel plutôt que caché (voir apps/api/src/lib/
  // schoolClass.ts).
  studentCount: number;
  // SCH-11 : `false` si cette classe appartient à une année scolaire NON active
  // (lecture seule — PATCH/DELETE la rejettent explicitement côté backend). Le
  // frontend s'en sert pour désactiver les actions de mutation AVANT même l'appel
  // réseau, plutôt que de laisser l'utilisateur découvrir le rejet après coup.
  isActiveYear: boolean;
  createdAt: string;
}

export interface CreateClassPayload {
  level: ClassLevel;
  // SCH-61 — écart SCH-50 corrigé : désormais OBLIGATOIRE, jamais posé après
  // coup via `updateClass()` seul (voir apps/api/src/routes/classes.ts).
  capacity: number;
}

export interface CreateClassResponse {
  schoolClass: SchoolClass;
}

export function createClass(
  client: ApiClient,
  payload: CreateClassPayload,
  accessToken: string,
): Promise<CreateClassResponse> {
  return client.post<CreateClassResponse>('/classes', payload, { accessToken });
}

// SCH-25 — génération RAPIDE de plusieurs classes d'un même niveau en une seule
// action. RACCOURCI de `createClass` ci-dessus, qui reste la création unitaire et
// n'est jamais remplacé. Même pattern recap/confirm que `createFeeConfiguration`
// (SCH-04) : `confirm` absent/`false` -> récapitulatif SANS aucune écriture,
// `confirm: true` -> création réelle. ⚠️ Les noms montrés dans le récapitulatif sont
// calculés par le SERVEUR (apps/api/src/lib/classBatchNaming.ts, qui compose
// `planClassCreation` de SCH-08) — jamais recalculés côté client, sans quoi la règle
// de nommage existerait en deux exemplaires.
export interface CreateClassBatchPayload {
  level: ClassLevel;
  count: number;
  // SCH-61 — écart SCH-50 corrigé : même effectif OBLIGATOIRE que la création
  // unitaire, appliqué IDENTIQUEMENT à chacune des classes du lot — requis dès
  // le récapitulatif (`confirm` absent), pas seulement à la confirmation.
  capacity: number;
  confirm?: boolean;
}

/** Renommage de la classe seule déjà existante, imposé par la règle SCH-08 (elle perd son absence de suffixe pour devenir "-A"). */
export interface ClassBatchRename {
  previousName: string;
  newName: string;
}

export interface CreateClassBatchRecap {
  kind: 'recap';
  recap: {
    level: ClassLevel;
    count: number;
    /** Noms exacts qui seront donnés aux classes créées, dans l'ordre de création. */
    createNames: string[];
    rename: ClassBatchRename | null;
  };
}

export interface CreateClassBatchCreated {
  kind: 'created';
  schoolClasses: SchoolClass[];
  renamed: ClassBatchRename | null;
}

export type CreateClassBatchResponse = CreateClassBatchRecap | CreateClassBatchCreated;

export function createClassBatch(
  client: ApiClient,
  payload: CreateClassBatchPayload,
  accessToken: string,
): Promise<CreateClassBatchResponse> {
  return client.post<CreateClassBatchResponse>('/classes/batch', payload, { accessToken });
}

export interface ListClassesResponse {
  classesByLevel: Partial<Record<ClassLevel, SchoolClass[]>>;
}

export function listClasses(
  client: ApiClient,
  accessToken: string,
  schoolYear?: string,
): Promise<ListClassesResponse> {
  return client.get<ListClassesResponse>('/classes', { accessToken, query: { schoolYear } });
}

export interface UpdateClassPayload {
  capacity: number | null;
}

export interface UpdateClassResponse {
  schoolClass: SchoolClass;
}

export function updateClass(
  client: ApiClient,
  classId: string,
  payload: UpdateClassPayload,
  accessToken: string,
): Promise<UpdateClassResponse> {
  return client.patch<UpdateClassResponse>(`/classes/${classId}`, payload, { accessToken });
}

export function deleteClass(client: ApiClient, classId: string, accessToken: string): Promise<void> {
  return client.delete<void>(`/classes/${classId}`, { accessToken });
}
